/**
 * Sync-code generation and end-to-end encryption.
 *
 * One 128-bit secret does two jobs, and the whole privacy story rests on how
 * they are separated:
 *
 *   docId = SHA-256(code ‖ "id")     — what the server stores things under
 *   key   = HKDF-SHA256(code, "key") — what encrypts the payload
 *
 * Both are one-way, so the server, which only ever learns `docId`, cannot walk
 * back to the code and therefore cannot derive the key. Anyone holding the
 * code can do both.
 *
 * HKDF rather than PBKDF2/Argon2 on purpose: the code is 128 bits straight out
 * of `crypto.getRandomValues`, not a human-chosen passphrase, so there is no
 * dictionary to slow an attacker down through. Stretching it would only cost
 * the user a slow unlock on every device.
 */

import { ENVELOPE_VERSION, type Envelope } from './protocol';

const CODE_BYTES = 16;
/** Crockford base32: no I, L, O or U, so nothing looks like 1, 0 or a swearword. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** 128 bits at 5 bits a character. */
const CODE_LENGTH = 26;
const GROUP = 4;

const IV_BYTES = 12;
const SALT = 'meowtracker:sync:v1';

/** Thrown when the payload will not decrypt: wrong code, or tampered-with data. */
export class DecryptError extends Error {
  constructor() {
    super('Could not decrypt — wrong sync code, or the data is damaged.');
    this.name = 'DecryptError';
  }
}

/**
 * `TextEncoder` is typed as `Uint8Array<ArrayBufferLike>`, which WebCrypto's
 * `BufferSource` rejects because it could in principle be a SharedArrayBuffer.
 * It never is here, so the assertion stays in this one helper rather than at
 * every call site.
 */
const utf8 = (s: string): Uint8Array<ArrayBuffer> =>
  new TextEncoder().encode(s) as Uint8Array<ArrayBuffer>;

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // Chunked: a spread of a large array blows the argument limit.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/** A fresh sync code: 26 Crockford-base32 characters, unformatted. */
export function generateCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(CODE_BYTES));
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += ALPHABET[(value >>> bits) & 31];
    }
  }
  // 128 bits is not a multiple of 5; the last 3 bits are padded out.
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 31];
  return out;
}

/**
 * Canonical form of whatever the user typed or pasted, or null if it cannot be
 * a code. Separators are ignored and the four characters Crockford omits are
 * folded onto their look-alikes, so a hand-copied code survives an l/1 or O/0
 * mix-up rather than failing with nothing to explain.
 */
export function normalizeCode(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .replace(/U/g, 'V');
  if (cleaned.length !== CODE_LENGTH) return null;
  for (const ch of cleaned) if (!ALPHABET.includes(ch)) return null;
  return cleaned;
}

/** Groups of four for display. The canonical form has no dashes. */
export function formatCode(code: string): string {
  return (code.match(new RegExp(`.{1,${GROUP}}`, 'g')) ?? []).join('-');
}

async function derive(code: string, purpose: 'id' | 'key'): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', utf8(`${SALT}|${purpose}|${code}`));
}

/** The public name of this checklist on the server. 128 bits of hash. */
export async function docIdFor(code: string): Promise<string> {
  const digest = new Uint8Array(await derive(code, 'id'));
  let hex = '';
  for (const byte of digest.subarray(0, 16)) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

async function keyFor(code: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', utf8(code), 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: utf8(SALT), info: utf8('meow-key') },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Encrypts `payload` into an envelope claiming revision `rev`. */
export async function seal(code: string, payload: unknown, rev: number): Promise<Envelope> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    await keyFor(code),
    utf8(JSON.stringify(payload)),
  );
  return {
    v: ENVELOPE_VERSION,
    rev,
    updatedAt: new Date().toISOString(),
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(cipher)),
  };
}

/** Reverses `seal`. Throws `DecryptError` for a wrong code or damaged data. */
export async function open(code: string, envelope: Envelope): Promise<unknown> {
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(envelope.iv) },
      await keyFor(code),
      fromBase64(envelope.data),
    );
  } catch {
    // AES-GCM authentication failure, or base64 that was never ciphertext.
    throw new DecryptError();
  }
  try {
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    throw new DecryptError();
  }
}
